import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useActiveContextReady } from './useActiveContextReady';
import { config } from '../config';
import type { DeviceType } from '@/types/deviceType';

export interface DeviceSummary {
  id: string;
  name: string;
  description: string | null;
  status: string;
  ipAddress: string | null;
  deviceType: DeviceType;
  regionId: string;
  regionName: string;
  divisionId: string | null;
  divisionName: string;
  /** Null until the device has been activated with an SNMP credential. */
  snmpCredentialId: string | null;
  /**
   * Polling is held off — an operator paused it, or billing degraded the
   * device for being over the plan limit. Dormant but still counted.
   */
  pollingPaused?: boolean;
}

interface DeviceListResponse {
  devices: DeviceSummary[];
}

export function useDevices(organizationId: string | undefined) {
  const contextReady = useActiveContextReady();

  return useQuery<DeviceSummary[]>({
    queryKey: ['devices', organizationId],
    queryFn: async () => {
      if (!organizationId) throw new Error('organizationId is required');
      const response = await axios.get<DeviceListResponse>(
        `${config.backendApi}/api/devices`,
        { withCredentials: true }
      );
      return response.data.devices;
    },
    enabled: !!organizationId && contextReady,
  });
}
